import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChartBar } from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from "recharts";
import { formatCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";

const compact = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
  if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  if (Math.abs(v) >= 1000) return `₹${Math.round(v / 1000)}k`;
  return `₹${v}`;
};

function TipBox({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-lg text-xs space-y-1" data-testid="bva-tooltip">
      <div className="font-semibold text-slate-900">{label}{d.job_name ? ` · ${d.job_name}` : ""}</div>
      <div className="flex justify-between gap-6"><span className="text-slate-500">Allocated</span><span className="font-medium">{formatCurrency(d.allocated)}</span></div>
      <div className="flex justify-between gap-6"><span className="text-slate-500">Actual Cost</span><span className="font-medium">{formatCurrency(d.actual)}</span></div>
      <div className="flex justify-between gap-6">
        <span className="text-slate-500">Variance</span>
        <span className={d.variance > 0 ? "font-medium text-red-600" : "font-medium text-emerald-600"}>
          {d.variance > 0 ? "+" : ""}{formatCurrency(d.variance)} ({d.variance_pct > 0 ? "+" : ""}{d.variance_pct}%)
        </span>
      </div>
      <div className="flex justify-between gap-6">
        <span className="text-slate-500">Profit</span>
        <span className={d.profit_pct >= 0 ? "font-medium text-emerald-600" : "font-medium text-red-600"}>{d.profit_pct}%</span>
      </div>
    </div>
  );
}

export default function BudgetVsActualChart({ jobs }) {
  const navigate = useNavigate();
  const [scope, setScope] = useState("active"); // active | all

  const rows = useMemo(() => {
    const list = (jobs || []).filter((j) => (scope === "active" ? j.job_status === "Active" : ["Active", "Completed", "Delivered"].includes(j.job_status)));
    return list
      .map((j) => {
        const allocated = Number(j.budget_allocated) || 0;
        const actual = Number(j.budget_spent) || 0;
        return {
          id: j.id,
          code: j.code,
          job_name: j.job_name,
          allocated,
          actual,
          variance: Math.round((actual - allocated) * 100) / 100,
          variance_pct: j.variance_pct,
          profit_pct: j.profitability,
        };
      })
      .sort((a, b) => b.allocated - a.allocated)
      .slice(0, 12);
  }, [jobs, scope]);

  const openJob = (d) => {
    const id = d?.payload?.id || d?.id;
    if (id) navigate(`/jobs/${id}`);
  };

  const totals = rows.reduce(
    (acc, r) => ({ allocated: acc.allocated + r.allocated, actual: acc.actual + r.actual }),
    { allocated: 0, actual: 0 }
  );
  const totalVariance = totals.actual - totals.allocated;

  return (
    <div data-testid="budget-vs-actual-chart">
      <div className="flex flex-wrap items-center justify-between gap-3 px-2 pb-3">
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <span className="text-slate-500">Allocated <span className="font-semibold text-slate-900">{formatCurrency(totals.allocated)}</span></span>
          <span className="text-slate-500">Actual <span className="font-semibold text-slate-900">{formatCurrency(totals.actual)}</span></span>
          <span className="text-slate-500">
            Variance{" "}
            <span className={totalVariance > 0 ? "font-semibold text-red-600" : "font-semibold text-emerald-600"} data-testid="bva-total-variance">
              {totalVariance > 0 ? "+" : ""}{formatCurrency(totalVariance)}
            </span>
          </span>
        </div>
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
          {[["active", "Active"], ["all", "All Costed"]].map(([k, lbl]) => (
            <Button
              key={k}
              size="sm"
              variant="ghost"
              onClick={() => setScope(k)}
              className={`h-7 px-3 text-xs ${scope === k ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
              data-testid={`bva-scope-${k}`}
            >
              {lbl}
            </Button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="h-[280px] flex flex-col items-center justify-center gap-2 text-sm text-slate-400">
          <ChartBar className="h-8 w-8" />
          No jobs with budgets in this view.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={rows}
            margin={{ top: 8, right: 12, left: 4, bottom: 4 }}
            barGap={4}
            className="cursor-pointer"
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="code" tick={{ fontSize: 11 }} stroke="#94a3b8" interval={0} angle={rows.length > 6 ? -35 : 0} textAnchor={rows.length > 6 ? "end" : "middle"} height={rows.length > 6 ? 55 : 30} />
            <YAxis tickFormatter={compact} tick={{ fontSize: 11 }} stroke="#94a3b8" width={62} />
            <Tooltip content={<TipBox />} cursor={{ fill: "#f1f5f9" }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="allocated" name="Budget Allocated" fill="#2563eb" radius={[4, 4, 0, 0]} maxBarSize={38} onClick={openJob} />
            <Bar dataKey="actual" name="Actual Cost" fill="#16a34a" radius={[4, 4, 0, 0]} maxBarSize={38} onClick={openJob}>
              {rows.map((r, i) => (
                <Cell key={i} fill={r.actual > r.allocated ? "#dc2626" : "#16a34a"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
      <p className="px-2 pt-2 text-xs text-slate-400">
        Green bars are within budget, red bars have overrun. Click a job to open its detail page.
      </p>
    </div>
  );
}
